import {
  ChangeDetectionStrategy,
  Component,
  computed,
  input,
  numberAttribute
} from '@angular/core';
import { OSKColorVariant } from '../../types/osk-theme.types';

/**
 * Atom: <osk-logo>
 * Renders the official OSKEY vector SVG wordmark (`LogoOskey.svg`) with Signal inputs.
 */
@Component({
  selector: 'osk-logo',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  styles: [
    `
      :host {
        display: inline-flex;
        align-items: center;
        line-height: 0;
      }

      .osk-logo {
        display: block;
        width: auto;
        flex-shrink: 0;
      }
    `
  ],
  template: `
    <svg
      class="osk-logo"
      [class]="'osk-logo osk-color--' + color()"
      [attr.width]="computedWidth()"
      [attr.height]="height()"
      [style.height.px]="height()"
      [style.color]="'var(--osk-color-' + color() + '-base, #5270FF)'"
      viewBox="0 0 115 22"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      role="img"
      [attr.aria-label]="ariaLabel()"
    >
      <g id="LogoOskey">
        <g id="OSKEY">
          <path
            d="M11.172 21.178C9.492 21.178 7.96133 20.9447 6.58 20.478C5.21733 19.9927 4.04133 19.302 3.052 18.406C2.08133 17.51 1.32533 16.4367 0.784 15.186C0.261333 13.9353 0 12.5353 0 10.986C0 9.45532 0.261333 8.07399 0.784 6.84199C1.32533 5.59132 2.09067 4.51799 3.08 3.62199C4.06933 2.72599 5.24533 2.03532 6.608 1.54999C7.97067 1.06466 9.492 0.821991 11.172 0.821991C12.8333 0.821991 14.3453 1.06466 15.708 1.54999C17.0893 2.03532 18.2653 2.71666 19.236 3.59399C20.2253 4.47132 20.9813 5.53532 21.504 6.78599C22.0453 8.03666 22.316 9.42732 22.316 10.958C22.316 12.5073 22.0453 13.9073 21.504 15.158C20.9813 16.4087 20.2253 17.482 19.236 18.378C18.2653 19.274 17.0893 19.9647 15.708 20.45C14.3453 20.9353 12.8333 21.178 11.172 21.178ZM11.172 17.202C12.46 17.202 13.5613 16.95 14.476 16.446C15.3907 15.942 16.1 15.2327 16.604 14.318C17.108 13.3847 17.36 12.274 17.36 10.986C17.36 9.67933 17.1173 8.56866 16.632 7.65399C16.1467 6.72066 15.4373 6.01132 14.504 5.52599C13.5707 5.04066 12.46 4.79799 11.172 4.79799C9.86533 4.79799 8.74533 5.04999 7.812 5.55399C6.89733 6.03932 6.188 6.74866 5.684 7.68199C5.19867 8.59666 4.956 9.70732 4.956 11.014C4.956 12.302 5.19867 13.4127 5.684 14.346C6.188 15.2607 6.90667 15.97 7.84 16.474C8.77333 16.9593 9.884 17.202 11.172 17.202Z"
            fill="currentColor"
          />
          <path
            d="M36.1972 21.178C35.0212 21.178 33.8639 21.094 32.7252 20.926C31.6052 20.758 30.5599 20.5153 29.5892 20.198C28.6186 19.862 27.7692 19.4607 27.0412 18.994L28.5532 15.326C29.3186 15.774 30.1119 16.1473 30.9332 16.446C31.7732 16.726 32.6412 16.9407 33.5372 17.09C34.4519 17.2207 35.4039 17.286 36.3932 17.286C37.8119 17.286 38.8759 17.1087 39.5852 16.754C40.3132 16.3993 40.6772 15.8767 40.6772 15.186C40.6772 14.7753 40.5652 14.458 40.3412 14.234C40.1172 13.9913 39.7906 13.8047 39.3612 13.674C38.9319 13.5247 38.3905 13.4033 37.7372 13.31L33.6212 12.694C31.5492 12.3953 30.0092 11.77 29.0012 10.818C28.0119 9.86599 27.5172 8.61532 27.5172 7.06599C27.5172 5.79666 27.8999 4.69532 28.6652 3.76199C29.4306 2.82866 30.4946 2.10999 31.8572 1.60599C33.2386 1.08332 34.8252 0.821991 36.6172 0.821991C37.6999 0.821991 38.7359 0.915324 39.7252 1.10199C40.7332 1.26999 41.6572 1.52199 42.4972 1.85799C43.3372 2.19399 44.0652 2.60466 44.6812 3.08999L43.1692 6.61799C42.2359 5.94599 41.2186 5.46066 40.1172 5.16199C39.0345 4.86332 37.8119 4.71399 36.4492 4.71399C35.5719 4.71399 34.8159 4.79799 34.1812 4.96599C33.5466 5.11532 33.0612 5.34866 32.7252 5.66599C32.4079 5.98332 32.2492 6.37532 32.2492 6.84199C32.2492 7.42066 32.4826 7.85932 32.9492 8.15799C33.4159 8.43799 34.1532 8.66199 35.1612 8.82999L39.1092 9.44599C41.1999 9.76332 42.7772 10.3793 43.8412 11.294C44.9052 12.2087 45.4372 13.4313 45.4372 14.962C45.4372 16.2127 45.0639 17.3047 44.3172 18.238C43.5706 19.1713 42.5066 19.8993 41.1252 20.422C39.7439 20.926 38.1012 21.178 36.1972 21.178Z"
            fill="currentColor"
          />
          <path
            d="M50.5046 20.87V1.12999H55.2926V9.61399H55.4046L64.6166 1.12999H70.5246L58.8766 11.546L59.0446 9.80999L71.1126 20.87H64.8686L55.4046 11.91H55.2926V20.87H50.5046Z"
            fill="currentColor"
          />
          <path
            d="M74.2717 20.87V1.12999H89.8117V4.85399H78.8917V8.99799H89.0277V12.694H78.8917V17.146H89.8117V20.87H74.2717Z"
            fill="currentColor"
          />
          <path
            d="M100.814 20.87V10.762L101.71 13.562L92.218 1.12999H97.706L103.586 8.94199H102.998L108.934 1.12999H114.226L104.734 13.562L105.63 10.762V20.87H100.814Z"
            fill="currentColor"
          />
        </g>
      </g>
    </svg>
  `
})
export class OSKLogoComponent {
  readonly color = input<OSKColorVariant>('primary');
  readonly height = input(22, { transform: numberAttribute });
  readonly ariaLabel = input<string>('OSKEY');

  protected readonly computedWidth = computed(() => Math.round(((this.height() * 115) / 22) * 100) / 100);
}
